import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, ExternalLink, Copy, Music } from "lucide-react";
import { toast } from "sonner";

const VIEWS = [
  { v: "welcome", l: "Begrüßungsbildschirm" },
  { v: "hall", l: "Saalanzeige" },
  { v: "menu", l: "Snackbar-Menü" },
  { v: "pickup", l: "Bestellabholung" },
  { v: "program", l: "Programmtafel" },
  { v: "vorschau", l: "Programmvorschau (7 Tage · Poster)" },
  { v: "thanks", l: "Dankesbildschirm" },
  { v: "tv", l: "Movie House TV" },
];

const EMPTY = { name: "", view: "welcome", hall: "Saal 1", music_url: "" };

export default function Displays() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const load = () => api.get("/displays").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post("/displays", form); toast.success("Erstellt"); setForm({ ...EMPTY }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setMusic = async (d, music_url) => {
    try {
      await api.patch(`/displays/${d.id}`, { ...d, music_url });
      toast.success(music_url ? "Musik gesetzt" : "Musik entfernt");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/displays/${id}`); load(); };
  const urlFor = (d) => `${window.location.origin}/display/${d.view}/${d.token}`;

  return (
    <div className="space-y-6" data-testid="displays-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Signage</div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Displays</h1>
      </div>
      <div className="card-lux p-4 grid md:grid-cols-5 gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Ansicht</label>
          <select value={form.view} onChange={(e) => setForm({ ...form, view: e.target.value })} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none">
            {VIEWS.map((v) => <option key={v.v} value={v.v}>{v.l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Saal</label>
          <input value={form.hall || ""} onChange={(e) => setForm({ ...form, hall: e.target.value })} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1"><Music size={10} /> Musik (optional)</label>
          <input value={form.music_url || ""} onChange={(e) => setForm({ ...form, music_url: e.target.value })}
            placeholder="YouTube-Link, Playlist oder .mp3"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-xs outline-none focus:border-[#D4AF37]" />
        </div>
        <button data-testid="display-create-btn" onClick={create} className="bg-[#D4AF37] text-black px-4 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] flex items-center justify-center gap-2"><Plus size={14} /> Erstellen</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((d) => (
          <div key={d.id} className="card-lux p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading text-xl">{d.name}</div>
                <div className="text-xs text-[#A8A398]">{VIEWS.find((v) => v.v === d.view)?.l} · {d.hall || ""}</div>
              </div>
              <button onClick={() => remove(d.id)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={12} /></button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input readOnly value={urlFor(d)} className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-xs font-mono outline-none" />
              <button onClick={() => { navigator.clipboard.writeText(urlFor(d)); toast.success("Kopiert"); }} className="text-xs px-2 py-2 border border-[#2A2A2A] rounded"><Copy size={12} /></button>
              <a href={urlFor(d)} target="_blank" rel="noreferrer" className="text-xs px-2 py-2 border border-[#2A2A2A] rounded hover:border-[#D4AF37]"><ExternalLink size={12} /></a>
            </div>
            <div className="mt-3 pt-3 border-t border-[#141414]">
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1 mb-1"><Music size={10} /> Hintergrund-Musik (optional)</label>
              <div className="flex items-center gap-2">
                <input defaultValue={d.music_url || ""} onBlur={(e) => e.target.value !== (d.music_url || "") && setMusic(d, e.target.value)}
                  data-testid={`display-music-${d.id}`}
                  placeholder="YouTube-Link, YouTube-Playlist oder direkte .mp3-URL"
                  className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-xs outline-none focus:border-[#D4AF37]" />
                {d.music_url && (
                  <button onClick={() => setMusic(d, "")} className="text-xs px-2 py-2 border border-[#2A2A2A] rounded hover:border-[#7A1921]" title="Musik entfernen">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <div className="text-[10px] text-[#575148] mt-1">Läuft auf dem Zuschauer-Bildschirm — Zuschauer sehen unten rechts einen &bdquo;Musik an&ldquo;-Schalter.</div>
            </div>
          </div>
        ))}
        {!rows.length && <div className="text-[#A8A398] text-sm">Noch keine Displays angelegt.</div>}
      </div>
    </div>
  );
}
